import { Link, Sheet } from '@cannect/new-components/atoms'
import React, { useState } from 'react'
import {
  LuChevronLeft,
  LuChevronRight,
  LuLogOut,
  LuMenu,
  LuPhone,
  LuShoppingBag,
  LuSmartphone,
  LuUser
} from 'react-icons/lu'
import { useHistory } from 'react-router-dom'
import { useHeader } from './useHeader'

const APP_LANDING_URL = 'https://atendimento.cannect.life/app-cannect'

type MobileMenuSheetProps = {
  isOpen: boolean
  onClose: () => void
  menuConfig: ReturnType<typeof useHeader>['menuConfig']
  headerCtas: ReturnType<typeof useHeader>['headerCtas']
  signedUserName: string
  isLogged: boolean
  handleLogout: () => void
}

type NavigationLevel = 'main' | 'submenu'

export const MobileMenuSheet = ({
  isOpen,
  onClose,
  menuConfig,
  headerCtas,
  signedUserName,
  isLogged,
  handleLogout
}: MobileMenuSheetProps) => {
  const [currentLevel, setCurrentLevel] = useState<NavigationLevel>('main')
  const [currentSubmenu, setCurrentSubmenu] = useState<any>(null)
  const history = useHistory()

  const handleClose = () => {
    setCurrentLevel('main')
    setCurrentSubmenu(null)
    onClose()
  }

  const handleRedirect = (path: string, external?: boolean) => {
    if (external) {
      window.open(path, '_blank', 'noopener noreferrer')
      handleClose()
    } else {
      history.push(path)
      handleClose()
    }
  }

  const handleSubmenuClick = (item: any, event: React.MouseEvent) => {
    if (item.submenu) {
      event.preventDefault()
      setCurrentSubmenu(item)
      setCurrentLevel('submenu')
    } else if (item.onClick) {
      event.preventDefault()
      item.onClick()
    } else {
      handleClose()
    }
  }

  const handleBack = () => {
    setCurrentLevel('main')
    setCurrentSubmenu(null)
  }

  // No mobile os dois CTAs da direita do header entram na lista, para não ficarem inacessíveis.
  const navItems = [...menuConfig, headerCtas.professionals]

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm"
          onClick={handleClose}
          style={{ backdropFilter: 'blur(4px)' }}
          role="button"
          tabIndex={0}
          aria-label="Fechar menu"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleClose()
            }
          }}
        />
      )}

      <Sheet.Root open={isOpen} onOpenChange={handleClose}>
        <Sheet.Content
          side="right"
          className="z-50 w-[320px] min-w-fit border-0 bg-white p-0 text-neutral-800 [&>button]:hidden"
          hideOverlay>
          <div className="flex h-full w-full flex-col bg-white">
            <div className="flex items-center justify-between border-b border-neutral-100 p-6">
              <div className="flex items-center gap-2">
                <LuMenu className="h-5 w-5 text-marketplace-800" />
                <span className="text-lg font-medium text-marketplace-800">Menu</span>
              </div>
              <button
                type="button"
                onClick={handleClose}
                aria-label="Fechar menu"
                className="rounded border-none bg-transparent p-1 transition-colors hover:bg-neutral-100">
                <span className="text-xl text-neutral-700">×</span>
              </button>
            </div>

            <div className="border-b border-neutral-100 px-6 py-4">
              {isLogged ? (
                <div className="flex items-center gap-3">
                  <LuUser className="h-5 w-5 text-marketplace-800" />
                  <span className="text-sm font-medium text-marketplace-800">Olá, {signedUserName}</span>
                </div>
              ) : (
                <Link
                  to="/login"
                  onClick={onClose}
                  className="flex items-center gap-3 text-marketplace-800 hover:text-marketplace-900">
                  <LuUser className="h-5 w-5" />
                  <span className="text-sm font-medium underline">Minha conta</span>
                </Link>
              )}
            </div>

            <div className="flex-1 overflow-y-auto">
              {currentLevel !== 'main' && (
                <div className="border-b border-neutral-100 px-6 py-4">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex items-center gap-2 border-none bg-transparent p-0 text-neutral-700 transition-colors hover:text-marketplace-800">
                    <LuChevronLeft className="h-4 w-4" />
                    <span className="text-sm font-medium">Voltar</span>
                  </button>
                </div>
              )}

              <nav className="py-2">
                {currentLevel === 'main' &&
                  navItems.map((item: any) => (
                    <Link
                      key={item.title}
                      to={item.path || '#'}
                      /* Sem isso o react-router trataria a URL absoluta como rota relativa —
                         "Conteúdos" já sofria disso antes de "Para profissionais" virar externo. */
                      isExternal={item.external}
                      target={item.external ? '_blank' : undefined}
                      rel={item.external ? 'noopener noreferrer' : undefined}
                      onClick={(e) => handleSubmenuClick(item, e)}
                      className="flex items-center justify-between px-6 py-4 text-neutral-800 transition-colors hover:bg-neutral-50">
                      <span className="text-base font-medium">{item.title}</span>
                      <LuChevronRight className="h-4 w-4 text-neutral-400" />
                    </Link>
                  ))}

                {currentLevel === 'submenu' && currentSubmenu && (
                  <>
                    <div className="px-6 py-2">
                      <span className="text-sm font-medium uppercase tracking-wide text-neutral-500">
                        {currentSubmenu.title}
                      </span>
                    </div>
                    {currentSubmenu.submenu?.map((subItem: any) => (
                      <Link
                        key={subItem.title}
                        to={subItem.path || '#'}
                        onClick={() => handleRedirect(subItem.path || '#', subItem.external)}
                        className="flex items-center justify-between px-6 py-4 text-neutral-800 transition-colors hover:bg-neutral-50">
                        <span className="text-base font-medium">{subItem.title}</span>
                        {subItem.external && <LuChevronRight className="h-4 w-4 text-neutral-400" />}
                      </Link>
                    ))}
                  </>
                )}
              </nav>
            </div>

            <div className="space-y-4 border-t border-neutral-100 p-6">
              <Link
                to={headerCtas.appointment.path || '/agendamento'}
                onClick={handleClose}
                className="flex items-center justify-center rounded-full border border-solid border-marketplace-900 bg-marketplace-800 px-4 py-2 text-sm font-semibold text-neutral-50 no-underline">
                {headerCtas.appointment.title}
              </Link>

              {isLogged && (
                <>
                  <Link
                    to="/conta?pagina=pedidos-todos"
                    onClick={handleClose}
                    className="flex items-center gap-3 text-neutral-800 hover:text-marketplace-800">
                    <LuShoppingBag className="h-5 w-5" />
                    <span className="text-sm font-medium underline">Meus pedidos</span>
                  </Link>

                  <button
                    type="button"
                    onClick={() => {
                      handleLogout()
                      handleClose()
                    }}
                    className="flex w-full items-center gap-3 border-none bg-transparent p-0 text-left text-neutral-800 hover:text-marketplace-800">
                    <LuLogOut className="h-5 w-5" />
                    <span className="text-sm font-medium underline">Sair</span>
                  </button>
                </>
              )}

              {/* Telefone e Baixar App saíram do header desktop no design novo — preservados aqui. */}
              <button
                type="button"
                onClick={() => window.open(APP_LANDING_URL, '_blank', 'noopener,noreferrer')}
                className="flex w-full items-center gap-3 border-none bg-transparent p-0 text-left text-neutral-800 hover:text-marketplace-800">
                <LuSmartphone className="h-5 w-5" />
                <span className="text-sm font-medium underline">Baixar App</span>
              </button>

              <a
                href="tel:+5511982711992"
                className="flex items-center gap-3 text-neutral-800 hover:text-marketplace-800">
                <LuPhone className="h-5 w-5" />
                <span className="text-sm font-medium underline">11 98271-1992</span>
              </a>
            </div>
          </div>
        </Sheet.Content>
      </Sheet.Root>
    </>
  )
}
